import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, SearchX } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared UI primitives.
 *
 * Built on Radix behaviour + CVA variants (the shadcn approach) rather than
 * pulled from a component library: the CLI generates these into your own
 * source anyway, and owning them means the enterprise palette and the density
 * this kind of data-heavy admin needs are ours to control.
 *
 * Grouped in one module because they share the same tokens and are almost
 * always imported together; splitting them into 15 files would add ceremony
 * without adding clarity.
 */

// =====================================================================
// Button
// =====================================================================

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ' +
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none ' +
    'disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        outline: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        // A loading button must also be disabled, or an impatient double-click
        // submits the form twice and creates duplicate records.
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" aria-hidden />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// =====================================================================
// Card
// =====================================================================

export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-lg border border-border bg-card text-card-foreground shadow-sm', className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1 border-b border-border px-5 py-4', className)} {...props} />
);

export const CardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn('text-base font-semibold leading-none tracking-tight', className)} {...props} />
);

export const CardDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-muted-foreground', className)} {...props} />
);

export const CardContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-5', className)} {...props} />
);

export const CardFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center gap-2 border-t border-border bg-muted/40 px-5 py-3', className)} {...props} />
);

// =====================================================================
// Input / Label / Select
// =====================================================================

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
        'transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Label = ({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label
    className={cn('text-sm font-medium leading-none text-foreground', className)}
    {...props}
  />
);

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

/** Label + control + error, so every form field is laid out identically. */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

// =====================================================================
// Badge
// =====================================================================

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted text-muted-foreground',
        info: 'border-transparent bg-accent text-accent-foreground',
        success: 'border-transparent bg-success/12 text-success',
        warning: 'border-transparent bg-warning/15 text-warning',
        danger: 'border-transparent bg-destructive/12 text-destructive',
        outline: 'border-border text-foreground',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

// =====================================================================
// Feedback states
// =====================================================================

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin text-muted-foreground', className)} aria-hidden />;
}

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
      <Spinner />
      {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} />;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <div className="text-muted-foreground/60">{icon ?? <SearchX className="size-8" />}</div>
      <div>
        <p className="font-medium text-foreground">{title}</p>
        {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <AlertCircle className="size-8 text-destructive" />
      <div>
        <p className="font-medium text-foreground">Something went wrong</p>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">{message}</p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

// =====================================================================
// Data table
// =====================================================================

export interface Column<T> {
  key: string;
  header: string;
  /** Column id accepted by the API's sort allow-list. Omit to disable sorting. */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  className?: string;
  render: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  empty?: React.ReactNode;
  sort?: string;
  order?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (row: T) => void;
}

/**
 * The one table used by every list screen.
 *
 * Sorting, loading skeletons, empty and error states are handled here, so a
 * new module gets consistent behaviour for free and a fix lands everywhere at
 * once.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  empty,
  sort,
  order,
  onSort,
  onRowClick,
}: DataTableProps<T>) {
  if (error) return <ErrorState message={error} />;

  if (loading) {
    return (
      <div className="space-y-2 p-5">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <>{empty ?? <EmptyState title="No records found" description="Try adjusting your filters." />}</>;
  }

  return (
    // Wide tables scroll inside their own container so the page body never
    // scrolls horizontally on a phone.
    <div className="scrollbar-thin w-full overflow-x-auto">
      <table className="w-full caption-bottom text-sm">
        <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur-sm">
          <tr className="border-b border-border">
            {columns.map((column) => {
              const isSorted = column.sortKey && sort === column.sortKey;
              const sortable = Boolean(column.sortKey && onSort);

              return (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={isSorted ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'eyebrow whitespace-nowrap px-3 py-3 text-muted-foreground',
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center',
                    !column.align && 'text-left',
                    sortable && 'cursor-pointer select-none hover:text-foreground',
                    column.className,
                  )}
                  onClick={sortable ? () => onSort?.(column.sortKey!) : undefined}
                >
                  {column.header}
                  {/* Fixed-width slot, so clicking a header does not nudge
                      every other column sideways. */}
                  <span aria-hidden className="ml-1 inline-block w-2 text-[9px] text-primary">
                    {isSorted ? (order === 'asc' ? '▲' : '▼') : ''}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-border/50 transition-colors last:border-0 hover:bg-accent/40',
                onRowClick && 'cursor-pointer',
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    'whitespace-nowrap px-3 py-3 text-[13px]',
                    column.align === 'right' && 'text-right tabular',
                    column.align === 'center' && 'text-center',
                    column.className,
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================================
// Pagination
// =====================================================================

export function Pagination({
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
      <p className="text-xs text-muted-foreground">
        {total === 0 ? (
          'No records'
        ) : (
          <>
            Showing <span className="font-medium text-foreground">{from.toLocaleString('en-IN')}</span>–
            <span className="font-medium text-foreground">{to.toLocaleString('en-IN')}</span> of{' '}
            <span className="font-medium text-foreground">{total.toLocaleString('en-IN')}</span>
          </>
        )}
      </p>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          <span className="px-3 text-xs text-muted-foreground">
            Page <span className="font-medium text-foreground">{page}</span> of {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Stat tile
// =====================================================================

export type StatTone = 'default' | 'primary' | 'success' | 'warning' | 'danger';

/**
 * Per-tone styling, written out rather than composed from template strings.
 *
 * Tailwind scans source text for complete class names; a class assembled at
 * runtime like `text-${tone}` is never emitted into the stylesheet and simply
 * does nothing. Every variant below has to appear literally.
 */
const STAT_TONES: Record<StatTone, { bar: string; chip: string; glow: string }> = {
  default: {
    bar: 'from-slate-400/70 to-slate-400/20',
    chip: 'bg-muted text-muted-foreground',
    glow: 'group-hover:shadow-slate-500/10',
  },
  primary: {
    bar: 'from-primary to-primary/30',
    chip: 'bg-primary/10 text-primary',
    glow: 'group-hover:shadow-primary/20',
  },
  success: {
    bar: 'from-emerald-500 to-emerald-500/30',
    chip: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    glow: 'group-hover:shadow-emerald-500/20',
  },
  warning: {
    bar: 'from-amber-500 to-amber-500/30',
    chip: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    glow: 'group-hover:shadow-amber-500/20',
  },
  danger: {
    bar: 'from-rose-500 to-rose-500/30',
    chip: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    glow: 'group-hover:shadow-rose-500/20',
  },
};

/**
 * Month-on-month delta.
 *
 * Colour follows *meaning*, not sign: for something like cancellations, up is
 * bad. `invert` lets the caller say so rather than every screen inventing its
 * own convention.
 */
function TrendPill({ percent, invert }: { percent: number; invert?: boolean }) {
  const flat = Math.abs(percent) < 0.5;
  const good = invert ? percent < 0 : percent > 0;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular',
        flat
          ? 'bg-muted text-muted-foreground'
          : good
            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
      )}
      // Screen readers get words; the arrow alone is not an accessible label.
      aria-label={`${flat ? 'flat' : percent > 0 ? 'up' : 'down'} ${Math.abs(percent).toFixed(1)} percent versus last month`}
    >
      {flat ? '–' : percent > 0 ? '▲' : '▼'}
      {Math.abs(percent).toFixed(percent >= 100 ? 0 : 1)}%
    </span>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  loading,
  trend,
  trendInvert,
  progress,
  index = 0,
  wrapLabel,
  wrapValue,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: StatTone;
  loading?: boolean;
  /** Percentage change against the previous period. */
  trend?: number | null;
  /** Set when a rise is bad news, e.g. cancellations. */
  trendInvert?: boolean;
  /** 0–100. Draws a progress rail along the bottom of the card. */
  progress?: number | null;
  /** Position in the row, used to stagger the entrance animation. */
  index?: number;
  /**
   * Vehicle Planning Phase 7G3C — opt-in only, defaults to false so every
   * existing caller keeps today's truncating label/value byte-for-byte.
   * Wraps the label onto multiple lines instead of clipping it with an
   * ellipsis; Payment Collection Summary is the only current caller that
   * needs this (long labels like "Total Outstanding" at narrow widths).
   */
  wrapLabel?: boolean;
  /** Same opt-in-only contract as wrapLabel, for the value line — see its
   *  own doc comment. Large Indian-grouped currency values (e.g.
   *  ₹15,03,682.43) wrap instead of clipping, with a narrower-width font
   *  step-down (22px/24px/27px) so a wrapped figure doesn't look
   *  oversized before there is room for it — every other caller keeps the
   *  flat 27px it has today. */
  wrapValue?: boolean;
}) {
  const styles = STAT_TONES[tone];

  return (
    <Card
      className={cn(
        'group relative overflow-hidden p-4 transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-lg',
        styles.glow,
      )}
      // Staggered so a row of tiles cascades instead of appearing all at once.
      style={{ animation: `rise-in 0.45s cubic-bezier(0.16,1,0.3,1) both`, animationDelay: `${index * 60}ms` }}
    >
      {/* Tone bar, replacing the old flat left border. */}
      <span className={cn('absolute inset-y-0 left-0 w-1 bg-gradient-to-b', styles.bar)} />

      <div className="flex items-start justify-between gap-3 pl-1.5">
        <div className="min-w-0 flex-1">
          <p className={cn('eyebrow text-muted-foreground', wrapLabel ? 'whitespace-normal break-words' : 'truncate')}>
            {label}
          </p>

          {loading ? (
            <Skeleton className="mt-2 h-8 w-28" />
          ) : (
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <p
                className={cn(
                  'metric',
                  wrapValue ? 'whitespace-normal break-words text-[22px] sm:text-[24px] 2xl:text-[27px]' : 'truncate text-[27px]',
                )}
              >
                {value}
              </p>
              {typeof trend === 'number' && <TrendPill percent={trend} invert={trendInvert} />}
            </div>
          )}

          {hint && <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>}
        </div>

        {icon && (
          <div
            className={cn(
              'grid size-10 shrink-0 place-items-center rounded-lg transition-transform duration-200',
              'group-hover:scale-105',
              styles.chip,
            )}
          >
            {icon}
          </div>
        )}
      </div>

      {typeof progress === 'number' && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full bg-gradient-to-r transition-all duration-700', styles.bar)}
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
    </Card>
  );
}

// =====================================================================
// Page header
// =====================================================================

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[22px] font-semibold leading-tight">{title}</h1>
        {description && (
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
